// components/filters/ListFilterModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";

const defaultPageSizeOptions = [10, 20, 50, 100];

// Yardımcı: item gruplu mu?
const isGrouped = (arr) =>
  Array.isArray(arr) && arr.some(it => Array.isArray(it.items) || Array.isArray(it.children));

export default function ListFilterModal({
  show,
  title,
  category,
  initialSelected = [],
  isKeywordCategory = false,
  fetchItems,               // () => Promise<[ flatItems ] or [ { groupLabel, items: [...] } ]>
  onApply,
  onClose,
  pageSizeOptions = defaultPageSizeOptions,
}) {
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(pageSizeOptions[0] || 20);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // local seçim (apply’e kadar parent’a yazma)
  const [localSelected, setLocalSelected] = useState(initialSelected);

  // open/reset
  useEffect(() => {
    if (!show) return;
    setQuery("");
    setPage(1);
    setLocalSelected(initialSelected);
    (async () => {
      setBusy(true);
      try {
        const data = await fetchItems(category);
        // If category is documentTypes, merge akademik/populer variants for display and sum counts
        const isDocType = category === 'documentTypes';
        const asArray = Array.isArray(data) ? data : [];

        const getCount = (it) => {
          const v = it?.value ?? it?.count ?? 0;
          const num = Number(v);
          return isNaN(num) ? 0 : num;
        };

        const normalize = (s) => String(s || '').toLowerCase();

        if (isDocType && isGrouped(asArray)) {
          // merge groups by normalized group label (remove akademik/popüler prefixes)
          const groupMap = new Map();
          asArray.forEach(g => {
            const rawLabel = String(g.groupLabel || '');
            const key = rawLabel.replace(/^\s*(akademik\s+|popüler\s+|populer\s+)/i, '').trim();
            const normKey = key || rawLabel;
            if (!groupMap.has(normKey)) groupMap.set(normKey, { groupLabel: key, items: [] });
            const target = groupMap.get(normKey);
            // concat items
            (g.items || g.children || []).forEach(it => {
              const existing = target.items.find(x => String(x.label || '').toLowerCase() === String(it.label || '').toLowerCase());
              if (existing) {
                const summed = getCount(existing) + getCount(it);
                existing.value = summed;
                existing.count = summed;
              } else {
                target.items.push({ ...it });
              }
            });
          });
          const mergedGroups = Array.from(groupMap.values());
          setItems(mergedGroups);
        } else if (isDocType && !isGrouped(asArray)) {
          // flat list: merge individual labels (akademik/popüler)
          const handled = [];
          const result = [];
          const bookVariants = ['akademik kitap', 'popüler kitap', 'populer kitap'];
          const bookSectionVariants = ['akademik kitap bölümü', 'popüler kitap bölümü', 'populer kitap bolumu', 'populer kitap bölümü', 'akademik kitap bolumu'];

          asArray.forEach(item => {
            const lbl = String(item.label || '').toLowerCase();
            if (bookVariants.includes(lbl)) {
              if (!handled.includes('kitap')) {
                const siblingCount = asArray.reduce((s, it) => {
                  const l = String(it.label || '').toLowerCase();
                  if (bookVariants.includes(l)) return s + getCount(it);
                  return s;
                }, 0);
                result.push({ ...item, label: 'Kitap', mergedLabels: ['Akademik Kitap', 'Popüler Kitap'], value: siblingCount, count: siblingCount });
                handled.push('kitap');
              }
            } else if (bookSectionVariants.includes(lbl)) {
              if (!handled.includes('kitapbolum')) {
                const siblingCount = asArray.reduce((s, it) => {
                  const l = String(it.label || '').toLowerCase();
                  if (bookSectionVariants.includes(l)) return s + getCount(it);
                  return s;
                }, 0);
                result.push({ ...item, label: 'Kitap Bölümü', mergedLabels: ['Akademik Kitap Bölümü', 'Popüler Kitap Bölümü'], value: siblingCount, count: siblingCount });
                handled.push('kitapbolum');
              }
            } else {
              result.push(item);
            }
          });
          setItems(result);
        } else {
          setItems(asArray);
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [show, category, fetchItems, initialSelected]);

  // checkbox seçili mi? now accepts item object
  const isChecked = (item) => {
    if (isKeywordCategory) {
      const label = (item && item.label) || item;
      return (localSelected || []).some(f => (f.searchkeyword ?? f) === label);
    }
    // non-keyword: item may be an object with mergedLabels
    if (!item) return false;
    if (typeof item === 'string') {
      return (localSelected || []).includes(item);
    }
    if (item.mergedLabels && Array.isArray(item.mergedLabels)) {
      return item.mergedLabels.some(lbl => (localSelected || []).includes(lbl));
    }
    return (localSelected || []).includes(item.label || item);
  };

  const toggleItem = (item) => {
    setLocalSelected(prev => {
      const sel = Array.isArray(prev) ? [...prev] : [];
      if (isKeywordCategory) {
        const label = (item && item.label) || item;
        const exists = sel.some(f => (f.searchkeyword ?? f) === label);
        return exists
          ? sel.filter(f => (f.searchkeyword ?? f) !== label)
          : [...sel, { searchkeyword: label }];
      }

      // non-keyword
      if (typeof item === 'string') {
        return sel.includes(item) ? sel.filter(s => s !== item) : [...sel, item];
      }

      // item object possibly with mergedLabels
      if (item.mergedLabels && Array.isArray(item.mergedLabels)) {
        const allSelected = item.mergedLabels.every(lbl => sel.includes(lbl));
        if (allSelected) {
          return sel.filter(s => !item.mergedLabels.includes(s));
        } else {
          const toAdd = item.mergedLabels.filter(lbl => !sel.includes(lbl));
          return [...sel, ...toAdd];
        }
      }

      // fallback: toggle by label
      const lab = item.label || item;
      return sel.includes(lab) ? sel.filter(s => s !== lab) : [...sel, lab];
    });
  };

  // ---- FLAT MOD (eski) ----
  const filteredFlat = useMemo(() => {
    if (isGrouped(items)) return []; // bu branch kullanılmayacak
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.label || "").toLowerCase().includes(q));
  }, [items, query]);

  const total = filteredFlat.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    if (isGrouped(items)) return [];
    const start = (currentPage - 1) * pageSize;
    return filteredFlat.slice(start, start + pageSize);
  }, [filteredFlat, currentPage, pageSize, items]);

  // ---- GROUPED MOD (yeni) ----
  // Beklenen biçim: [{ groupLabel: "Akademik Kitap", items: [{label, value?}, ...] }, ...]
  // Aramada çocuklarda eşleşme varsa o grup render’lanır (sadece eşleşen çocukları gösterir).
  const filteredGroups = useMemo(() => {
    if (!isGrouped(items)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items
      .map(g => {
        const children = (g.items || g.children || []).filter(ch =>
          (ch.label || "").toLowerCase().includes(q)
        );
        if (children.length === 0) return null;
        return { ...g, items: children };
      })
      .filter(Boolean);
  }, [items, query]);

  if (!show) return null;

  const grouped = isGrouped(items);

  console.log('modal apply', category, localSelected)

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content rounded-3">
          <div className="modal-header">
            <h5 className="modal-title">{title || "Filtre Seçenekleri"}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="d-flex gap-2 align-items-center mb-3">
              <div className="flex-grow-1">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FeatherIcon icon="search" size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                  />
                </div>
              </div>

              {/* Gruplu modda sayfalama yok */}
              {!grouped && (
                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted">Page size</span>
                  <select
                    className="form-select"
                    style={{ width: 100 }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    {pageSizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* ---- CONTENT ---- */}
            <div className="list-group border rounded-3">
              {busy && <div className="p-4 text-center text-muted">Yükleniyor…</div>}

              {/* Gruplu render */}
              {!busy && grouped && filteredGroups.map((grp, gi) => (
                <div key={`grp-${gi}`} className="py-2 px-3 border-bottom">
                  <div className="fw-semibold mb-2 text-newdarkfume">{grp.groupLabel}</div>
                  {(grp.items || []).map((it, idx) => {
                    const lid = `modal-${category}-g-${gi}-${idx}`;
                    return (
                      <label
                        key={lid}
                        className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-1"
                        htmlFor={lid}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-start">
                          <input
                            id={lid}
                            type="checkbox"
                            className="form-check-input me-2 mt-0"
                            checked={isChecked(it)}
                            onChange={() => toggleItem(it)}
                          />
                          <span className="text-darkfume">{it.label}</span>
                        </div>
                        {(it.count ?? it.value) != null && (
                          <span className="badge bg-light text-muted">
                            {(it.count ?? it.value)?.toLocaleString?.("tr-TR") ?? it.count ?? it.value}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              ))}

              {/* Düz render */}
              {!busy && !grouped && pageItems.map((it, idx) => {
                const lid = `modal-${category}-${idx}`;
                return (
                  <label key={lid} className="list-group-item d-flex justify-content-between align-items-center" htmlFor={lid} style={{ cursor:"pointer" }}>
                    <div className="d-flex align-items-start">
                      <input
                        id={lid}
                        type="checkbox"
                        className="form-check-input me-2 mt-0"
                        checked={isChecked(it)}
                        onChange={() => toggleItem(it)}
                      />
                      <span className="text-darkfume">{it.label}</span>
                    </div>
                    {(it.count ?? it.value) != null && (
                      <span className="badge bg-light text-muted">
                        {(it.count ?? it.value)?.toLocaleString?.("tr-TR") ?? it.count ?? it.value}
                      </span>
                    )}
                  </label>
                );
              })}

              {!busy && (
                grouped
                  ? (filteredGroups.length === 0 && <div className="p-4 text-center text-muted">Sonuç yok.</div>)
                  : (pageItems.length === 0 && <div className="p-4 text-center text-muted">Sonuç yok.</div>)
              )}
            </div>

            {/* Gruplu modda sayaç metni de farklı */}
            {!grouped && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  {total.toLocaleString("tr-TR")} sonuç — Sayfa {currentPage}/{totalPages}
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary btn-sm" disabled={currentPage===1} onClick={()=>setPage(p=>Math.max(1,p-1))}>
                    <FeatherIcon icon="chevron-left" size={16} /> Önceki
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" disabled={currentPage===totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>
                    Sonraki <FeatherIcon icon="chevron-right" size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-danger" onClick={onClose}>Vazgeç</button>
            <button
              className="btn btn-primary"
              onClick={() => onApply?.(category, localSelected)}
            >
              Uygula
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
